import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import OffersSection from "../components/OffersSection";
import {
  ArrowRight, Search, Info, Plus, Sparkles, Phone,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  BREEDS, PRICING_COLUMNS, ADD_ONS, SPECIAL_TREATMENT, PRICING_NOTES, FAQS, BRAND,
} from "../data/mock";

const formatPrice = (v) => (v == null ? "—" : `₹${v.toLocaleString("en-IN")}`);

export default function Pricing() {
  const [query, setQuery] = useState("");
  const [activeBreed, setActiveBreed] = useState(BREEDS[0].name);

  const filteredBreeds = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return BREEDS;
    return BREEDS.filter((b) => b.name.toLowerCase().includes(q));
  }, [query]);

  const current = BREEDS.find((b) => b.name === activeBreed) || BREEDS[0];

  return (
    <div data-testid="pricing-page" className="bg-[#3915AA]">
      {/* Header */}
      <section className="section-padding container-custom">
        <div className="max-w-3xl">
          <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-4">
            Transparent pricing
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.02]">
            Premium care at <span className="text-gradient-primary">honest prices</span>.
          </h1>
          <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-2xl">
            Pricing is breed-specific and adjusted by age — exactly how a fair pet groomer should
            price. Pick your pet's breed below to see the rate card.
          </p>
        </div>
      </section>

      {/* Breed selector + Pricing Table */}
      <section className="container-custom py-16 md:py-20 section-light">
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Breed list / search */}
          <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start">
            <div className="rounded-3xl border border-white/10 bg-[#2E0F94] p-5">
              <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">
                Find your breed
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  data-testid="pricing-breed-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search e.g. Husky, Pug, Cat…"
                  className="w-full bg-[#1F0A66]/50 border border-white/10 rounded-xl pl-9 pr-3 py-3 text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none transition-all"
                />
              </div>

              <div className="mt-4 max-h-[420px] overflow-y-auto pr-1 -mr-1">
                {filteredBreeds.length === 0 ? (
                  <p className="text-white/50 text-sm text-center py-6">
                    No breed found. Call us — we groom every breed.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {filteredBreeds.map((b) => (
                      <li key={b.name}>
                        <button
                          onClick={() => setActiveBreed(b.name)}
                          data-testid={`breed-btn-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                          className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all flex items-center justify-between group ${
                            activeBreed === b.name
                              ? "bg-[#FC5106] text-white"
                              : "text-white/70 hover:bg-white/5 hover:text-white"
                          }`}
                        >
                          <span>{b.name}</span>
                          <span
                            className={`text-[10px] uppercase tracking-wider ${
                              activeBreed === b.name ? "text-white/80" : "text-white/40"
                            }`}
                          >
                            {b.category}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </aside>

          {/* Pricing table */}
          <div className="lg:col-span-8">
            <div className="rounded-3xl border border-white/10 bg-[#2E0F94] overflow-hidden">
              <div className="p-6 md:p-8 border-b border-white/10 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-[#C9BCFF] text-xs uppercase tracking-[0.2em] mb-1">
                    {current.category} breed
                  </p>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
                    {current.name}
                  </h2>
                </div>
                <Link
                  to="/contact"
                  data-testid="pricing-book-current-breed"
                  className="btn-primary !py-3 !px-5 !text-sm"
                >
                  Book {current.name} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left" data-testid="pricing-table-desktop">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-white/50">
                      <th className="px-6 py-4 font-medium">Age</th>
                      {PRICING_COLUMNS.map((c) => (
                        <th key={c.key} className="px-4 py-4 font-medium">
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {current.rows.map((row, i) => (
                      <tr
                        key={row.age}
                        className={`border-t border-white/5 ${
                          i % 2 === 1 ? "bg-white/[0.015]" : ""
                        }`}
                      >
                        <td className="px-6 py-4 text-white font-semibold whitespace-nowrap">{row.age}</td>
                        {PRICING_COLUMNS.map((c) => (
                          <td
                            key={c.key}
                            className={`px-4 py-4 ${
                              row[c.key] == null ? "text-white/30" : "text-white"
                            }`}
                          >
                            <span
                              className={
                                row[c.key] != null
                                  ? "font-heading font-semibold text-base"
                                  : ""
                              }
                            >
                              {formatPrice(row[c.key])}
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden p-4 space-y-3" data-testid="pricing-table-mobile">
                {current.rows.map((row) => (
                  <div
                    key={row.age}
                    className="rounded-2xl border border-white/10 bg-[#1F0A66]/30 p-5"
                  >
                    <p className="text-[#C9BCFF] text-xs uppercase tracking-wider mb-3 font-semibold">
                      {row.age}
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      {PRICING_COLUMNS.map((c) => (
                        <div key={c.key}>
                          <p className="text-[11px] text-white/50 mb-1">{c.label}</p>
                          <p
                            className={`font-heading font-bold text-lg ${
                              row[c.key] == null ? "text-white/30" : "text-white"
                            }`}
                          >
                            {formatPrice(row[c.key])}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="px-6 md:px-8 py-5 bg-[#1F0A66]/40 border-t border-white/10 flex items-start gap-3">
                <Info className="w-4 h-4 text-[#FC5106] mt-0.5 flex-shrink-0" />
                <p className="text-white/60 text-xs leading-relaxed">
                  "—" means service not offered for this breed/age. Final price may vary based on
                  coat condition, matting, or special handling needs. We'll always confirm before
                  starting.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="container-custom pt-20 pb-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div className="max-w-2xl">
            <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-3">Add-ons</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight">
              Mix & match <span className="text-gradient-primary">extras</span>.
            </h2>
            <p className="text-white/60 mt-3 text-base">
              Add any of these to a bath or grooming session.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {ADD_ONS.map((a) => (
            <div
              key={a.name}
              data-testid={`addon-${a.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="rounded-2xl border border-white/10 bg-[#2E0F94] p-5 hover:border-[#FC5106]/50 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-2">
                <Plus className="w-4 h-4 text-[#FC5106]" />
                <span className="text-[10px] uppercase tracking-wider text-white/40">
                  Add-on
                </span>
              </div>
              <p className="text-white font-semibold text-sm md:text-base mb-2 leading-tight">
                {a.name}
              </p>
              <p className="font-heading font-bold text-2xl text-gradient-primary">
                ₹{a.price}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Special treatment */}
      <section className="container-custom pb-20">
        <div className="relative rounded-[32px] overflow-hidden border border-[#FC5106]/30 bg-gradient-to-br from-[#FC5106]/15 via-[#2E0F94] to-white/10 p-8 md:p-12 grain">
          <div className="absolute -top-12 -right-12 w-64 h-64 bg-[#FC5106]/20 blur-3xl rounded-full" />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#1F0A66]/40 border border-white/10 rounded-full px-4 py-2 mb-5">
                <Sparkles className="w-4 h-4 text-[#FC5106]" />
                <span className="text-xs uppercase tracking-[0.2em] text-white/80">
                  Special treatment
                </span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white leading-tight mb-4">
                {SPECIAL_TREATMENT.name}
              </h2>
              <p className="text-white/75 text-base leading-relaxed mb-6">
                {SPECIAL_TREATMENT.description}
              </p>
              <Link to="/contact" data-testid="special-treatment-cta" className="btn-primary">
                Book this treatment <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="text-center md:text-right">
              <p className="text-white/60 text-sm uppercase tracking-wider mb-2">
                Starting from
              </p>
              <p className="font-heading text-7xl md:text-8xl font-bold text-gradient-primary leading-none">
                ₹{SPECIAL_TREATMENT.price}
              </p>
              <p className="text-white/50 text-sm mt-3">Per session</p>
            </div>
          </div>
        </div>
      </section>

      {/* Notes */}
      <OffersSection source="pricing" />

      <section className="container-custom pb-20">
        <div className="rounded-3xl border border-white/10 bg-[#2E0F94] p-7 md:p-9">
          <div className="flex items-start gap-3 mb-5">
            <Info className="w-5 h-5 text-[#C9BCFF] mt-0.5 flex-shrink-0" />
            <h3 className="font-heading text-xl md:text-2xl font-bold text-white">
              Good to know
            </h3>
          </div>
          <ul className="space-y-3">
            {PRICING_NOTES.map((n, i) => (
              <li
                key={i}
                data-testid={`pricing-note-${i}`}
                className="text-white/70 text-sm md:text-base leading-relaxed pl-2 border-l-2 border-[#FC5106]/40"
              >
                <span className="pl-3 block">{n}</span>
              </li>
            ))}
          </ul>
          <a
            href={`tel:${BRAND.phoneRaw}`}
            data-testid="pricing-call-cta"
            className="btn-secondary mt-7 !py-3 !px-6 !text-sm"
          >
            <Phone className="w-4 h-4" /> Call {BRAND.phone}
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-custom pb-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-4">
              Frequently asked
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
              Questions, answered.
            </h2>
            <p className="text-white/70 leading-relaxed mb-8">
              Still wondering something? Call or WhatsApp us and we'll happily help you pick the
              right service for your pet.
            </p>
            <Link to="/contact" data-testid="pricing-contact-cta" className="btn-primary">
              Talk to us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border border-white/10 rounded-2xl bg-[#2E0F94] overflow-hidden px-5"
                >
                  <AccordionTrigger className="text-left text-white hover:no-underline font-heading text-base md:text-lg py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/70 leading-relaxed pb-5 text-sm md:text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
}
